// HDT Slider Component - Infinite carousel slider for video stories
class HDTSlider extends HTMLElement {
  constructor() {
    super();
    /** @type {{navUI?: boolean, dotUI?: boolean, loop?: boolean, autoPlay?: number}} */
    this.config = {};
    /** @type {number} */
    this.currentIndex = 0;
    /** @type {boolean} */
    this.isTransitioning = false;
    /** @type {number | null} */
    this.autoPlayInterval = null;
    /** @type {number} */
    this.touchStartX = 0;
    /** @type {number} */
    this.touchEndX = 0;
    /** @type {boolean} */
    this.isMobile = window.innerWidth < 768;
    /** @type {Element | null} */
    this.viewport = null;
    /** @type {HTMLElement | null} */
    this.container = null;
    /** @type {Element[]} */
    this.slides = [];
    /** @type {Element[]} */
    this.originalSlides = [];
    /** @type {number} */
    this.slidesCount = 0;
    /** @type {Element | null} */
    this.prevButton = null;
    /** @type {Element | null} */
    this.nextButton = null;
    /** @type {Element | null} */
    this.dotsContainer = null;
  }

  connectedCallback() {
    // Parse config
    const configAttr = this.getAttribute('config');
    if (configAttr) {
      try {
        this.config = JSON.parse(configAttr);
      } catch (e) {
        console.error('Failed to parse slider config:', e);
        this.config = {};
      }
    }

    this.viewport = this.querySelector('.hdt-slider__viewport');
    this.container = /** @type {HTMLElement | null} */ (this.querySelector('.hdt-slider__container'));
    this.slides = Array.from(this.querySelectorAll('.hdt-slider__slide'));
    this.prevButton = this.querySelector('.hdt-slider__button--prev');
    this.nextButton = this.querySelector('.hdt-slider__button--next');
    this.dotsContainer = this.querySelector('[hdt-slider-dots]');

    if (!this.container || this.slides.length === 0) return;

    this.originalSlides = [...this.slides];
    this.slidesCount = this.originalSlides.length;

    // Check if mobile
    this.checkMobile();
    window.addEventListener('resize', () => this.handleResize());

    // Initialize only if loop is enabled or on mobile
    if (this.config.loop || this.isMobile) {
      this.init();
    } else {
      // Fallback to basic scroll behavior
      this.initBasicScroll();
    }
  }

  checkMobile() {
    this.isMobile = window.innerWidth < 768;
  }

  handleResize() {
    const wasMobile = this.isMobile;
    this.checkMobile();
    
    if (wasMobile !== this.isMobile) {
      // Reinitialize if mobile state changed
      this.destroy();
      if (this.config.loop || this.isMobile) {
        this.init();
      } else {
        this.initBasicScroll();
      }
    }
  }

  init() {
    // Clone slides for infinite loop
    if (this.config.loop && this.slidesCount > 1) {
      this.createClones();
    }

    // Setup navigation
    this.setupNavigation();

    // Setup dots
    if (this.config.dotUI) {
      this.setupDots();
    }

    // Setup touch events for mobile
    if (this.isMobile) {
      this.setupTouchEvents();
    }

    // Setup auto play
    if (this.config.autoPlay && this.config.autoPlay > 0) {
      this.startAutoPlay();
    }

    // Initial position
    if (this.config.loop && this.slidesCount > 1) {
      this.goToSlide(this.slidesCount, false);
    } else {
      this.updateSlidePosition(false);
    }
  }

  initBasicScroll() {
    // Setup navigation for scroll
    this.setupNavigation();
    
    // Setup dots
    if (this.config.dotUI) {
      this.setupDots();
    }

    // Listen to scroll events
    if (this.viewport) {
      this.viewport.addEventListener('scroll', () => this.handleScroll());
    }
  }

  handleScroll() {
    // Update active dot based on scroll position
    if (!this.dotsContainer || !this.viewport) return;

    const scrollLeft = /** @type {HTMLElement} */ (this.viewport).scrollLeft;
    const slideWidth = this.slides[0] ? /** @type {HTMLElement} */ (this.slides[0]).offsetWidth : 0;
    const gap = this.container ? parseInt(getComputedStyle(this.container).gap) || 0 : 0;
    
    const index = Math.round(scrollLeft / (slideWidth + gap));
    this.updateDots(index);
  }

  createClones() {
    // Clone first and last slides for seamless loop
    const firstClones = this.originalSlides.slice(0, this.slidesCount).map(/** @param {Element} slide */ slide => {
      const clone = /** @type {Element} */ (slide.cloneNode(true));
      clone.classList.add('hdt-clone');
      return clone;
    });

    const lastClones = this.originalSlides.slice(0, this.slidesCount).map(/** @param {Element} slide */ slide => {
      const clone = /** @type {Element} */ (slide.cloneNode(true));
      clone.classList.add('hdt-clone');
      return clone;
    });

    // Append clones
    if (this.container) {
      lastClones.forEach(/** @param {Element} clone */ clone => this.container?.appendChild(clone));
      firstClones.reverse().forEach(/** @param {Element} clone */ clone => this.container && this.container.insertBefore(clone, this.container.firstChild));
    }

    // Update slides array
    this.slides = this.container ? Array.from(this.container.querySelectorAll('.hdt-slider__slide')) : [];
    this.currentIndex = this.slidesCount; // Start at first real slide
  }

  setupNavigation() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.prev());
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.next());
    }
  }

  setupDots() {
    if (!this.dotsContainer) return;

    this.dotsContainer.innerHTML = '';

    for (let i = 0; i < this.slidesCount; i++) {
      const dot = document.createElement('button');
      dot.classList.add('hdt-slider__dot');
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.addEventListener('click', () => {
        if (this.config?.loop) {
          this.goToSlide(i + this.slidesCount);
        } else {
          this.scrollToSlide(i);
        }
      });
      this.dotsContainer?.appendChild(dot);
    }

    this.updateDots(0);
  }

  /**
   * @param {number} index
   */
  updateDots(index) {
    if (!this.dotsContainer) return;

    const dots = this.dotsContainer.querySelectorAll('.hdt-slider__dot');
    const realIndex = this.config?.loop ? index % this.slidesCount : index;

    dots.forEach((dot, i) => {
      if (i === realIndex) {
        dot.classList.add('is-active');
      } else {
        dot.classList.remove('is-active');
      }
    });
  }

  setupTouchEvents() {
    if (!this.viewport) return;

    this.viewport.addEventListener('touchstart', (/** @type {TouchEvent} */ e) => {
      this.touchStartX = e.touches[0]?.clientX || 0;
      this.stopAutoPlay();
    }, { passive: true });

    this.viewport.addEventListener('touchmove', (/** @type {TouchEvent} */ e) => {
      this.touchEndX = e.touches[0]?.clientX || 0;
    }, { passive: true });

    this.viewport.addEventListener('touchend', () => {
      this.handleSwipe();
      if (this.config?.autoPlay && this.config.autoPlay > 0) {
        this.startAutoPlay();
      }
    });
  }

  handleSwipe() {
    const swipeThreshold = 50;
    const diff = this.touchStartX - this.touchEndX;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        // Swipe left - next
        this.next();
      } else {
        // Swipe right - prev
        this.prev();
      }
    }
  }

  next() {
    if (this.isTransitioning) return;
    
    if (this.config?.loop) {
      this.currentIndex++;
      this.goToSlide(this.currentIndex);
    } else {
      if (this.currentIndex < this.slidesCount - 1) {
        this.currentIndex++;
        this.scrollToSlide(this.currentIndex);
      }
    }
  }

  prev() {
    if (this.isTransitioning) return;
    
    if (this.config?.loop) {
      this.currentIndex--;
      this.goToSlide(this.currentIndex);
    } else {
      if (this.currentIndex > 0) {
        this.currentIndex--;
        this.scrollToSlide(this.currentIndex);
      }
    }
  }

  /**
   * @param {number} index
   * @param {boolean} animate
   */
  goToSlide(index, animate = true) {
    if (this.isTransitioning) return;

    this.currentIndex = index;
    this.updateSlidePosition(animate);

    // Handle infinite loop
    if (this.config?.loop && animate) {
      this.isTransitioning = true;

      setTimeout(() => {
        if (this.currentIndex >= this.slides.length - this.slidesCount) {
          // Jump to first real slide
          this.currentIndex = this.slidesCount;
          this.updateSlidePosition(false);
        } else if (this.currentIndex < this.slidesCount) {
          // Jump to last real slide
          this.currentIndex = this.slides.length - this.slidesCount - 1;
          this.updateSlidePosition(false);
        }
        this.isTransitioning = false;
      }, 300);
    }

    // Update dots
    const realIndex = this.currentIndex - this.slidesCount;
    this.updateDots(realIndex >= 0 ? realIndex : 0);
  }

  /**
   * @param {number} index
   */
  scrollToSlide(index) {
    if (!this.viewport) return;
    
    const slide = this.slides[index];
    if (!slide) return;

    const slideWidth = /** @type {HTMLElement} */ (slide).offsetWidth;
    const gap = this.container ? parseInt(getComputedStyle(this.container).gap) || 0 : 0;
    
    /** @type {HTMLElement} */ (this.viewport).scrollTo({
      left: index * (slideWidth + gap),
      behavior: 'smooth'
    });

    this.currentIndex = index;
    this.updateDots(index);
  }

  updateSlidePosition(animate = true) {
    if (!this.container) return;

    const slideWidth = this.slides[0] ? /** @type {HTMLElement} */ (this.slides[0]).offsetWidth : 0;
    const gap = parseInt(getComputedStyle(this.container).gap) || 0;
    
    // Calculate offset to center the current slide
    const viewportWidth = this.viewport ? /** @type {HTMLElement} */ (this.viewport).offsetWidth : 0;
    const centerOffset = (viewportWidth - slideWidth) / 2;
    const offset = -(this.currentIndex * (slideWidth + gap)) + centerOffset;

    if (animate) {
      this.container.style.transition = 'transform 0.3s ease-in-out';
    } else {
      this.container.style.transition = 'none';
    }

    this.container.style.transform = `translateX(${offset}px)`;
    
    // Update active class for center zoom effect
    this.updateActiveSlide();
  }

  /**
   * Update active class on center slide
   */
  updateActiveSlide() {
    this.slides.forEach((slide, index) => {
      slide.classList.remove('is-active', 'hdt-active');
      if (index === this.currentIndex) {
        slide.classList.add('is-active', 'hdt-active');
      }
    });
  }

  startAutoPlay() {
    this.stopAutoPlay();
    this.autoPlayInterval = setInterval(() => {
      this.next();
    }, this.config?.autoPlay || 3000);
  }

  stopAutoPlay() {
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
      this.autoPlayInterval = null;
    }
  }

  destroy() {
    this.stopAutoPlay();
    
    if (this.prevButton) {
      this.prevButton.removeEventListener('click', () => this.prev());
    }
    
    if (this.nextButton) {
      this.nextButton.removeEventListener('click', () => this.next());
    }

    // Remove clones
    if (this.container) {
      const clones = this.container.querySelectorAll('.hdt-clone');
      clones.forEach(clone => clone.remove());

      // Reset
      this.currentIndex = 0;
      this.slides = [...this.originalSlides];
      this.container.style.transform = '';
      this.container.style.transition = '';
    }
  }

  disconnectedCallback() {
    this.destroy();
    window.removeEventListener('resize', () => this.handleResize());
  }
}

// Register custom element
if (!customElements.get('hdt-slider')) {
  customElements.define('hdt-slider', HDTSlider);
}
